import { useState } from 'react';
import { cloneDeep } from 'lodash';

import { InvoiceState } from 'containers/SettlementRequest/graphQL/types';
import { useLazyQueryGetSettlement } from '../graphQL/hooks/useLazyQueryGetSettlement';
import { useQueryGetInvoiceList } from '../graphQL/hooks/useQueryGetInvoiceList';
import { InvoiceNode } from '../graphQL/types';

export const requiredStatuses = [
  InvoiceState.CANCELLED,
  InvoiceState.COMPLETED,
  InvoiceState.DRAFT_COMPLETED,
  InvoiceState.FAILED,
  InvoiceState.PROGRESSING,
  InvoiceState.READY,
  InvoiceState.RECEIVED,
  InvoiceState.RECEIVE_REQUESTED,
];

export interface CustomDate {
  start: string;
  end: string;
}

export const useSettlementRequestListHooks = () => {
  const {
    invoices,
    isLoading,
    invoiceFilter,
    setInvoiceFilter,
    handleRefetchInvoice,
    handleLoadMoreInovice,
  } = useQueryGetInvoiceList();
  const { settlements, handleGetSettlement } = useLazyQueryGetSettlement();

  const [isOpenDetailModal, setIsOpenDetailModal] = useState<boolean>(false);
  const [isOpenSearchModal, setIsOpenSearchModal] = useState<boolean>(false);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceNode>();
  const [termType, setTermType] = useState<string>('all');
  const [statusIn, setStatusIn] = useState<InvoiceState[]>(requiredStatuses);
  const [customDate, setCustomDate] = useState<CustomDate>({
    start: '',
    end: '',
  });

  const isStatusAllChecked = requiredStatuses.every(status =>
    statusIn.includes(status),
  );

  const isAcceptFilter =
    invoiceFilter.filter.term !== null ||
    !requiredStatuses.every(status =>
      invoiceFilter.filter.statusIn.includes(status),
    );

  const handleOpenDetailModal = (invoice: InvoiceNode) => {
    setIsOpenDetailModal(true);

    setSelectedInvoice(invoice);
    handleGetSettlement(invoice.id);
  };

  const handleCloseDetailModal = () => {
    setIsOpenDetailModal(false);
  };

  const handleSetTermType = (type: string) => {
    setTermType(type);
  };

  const progressingStates = [
    InvoiceState.DRAFT_COMPLETED,
    InvoiceState.PROGRESSING,
    InvoiceState.READY,
  ];

  const handleSetInvoiceState = (
    status: InvoiceState,
    isAllChecked?: boolean,
  ) => {
    if (isAllChecked) {
      setStatusIn(requiredStatuses);

      return;
    }

    let copyStatus = cloneDeep(statusIn);

    if (isStatusAllChecked) {
      setStatusIn([status]);

      return;
    }

    if (statusIn.includes(status)) {
      copyStatus = copyStatus.filter(item => item !== status);
    } else {
      copyStatus = [...copyStatus, status];
    }

    setStatusIn(copyStatus);
  };

  const handleSetProgressingState = () => {
    if (isStatusAllChecked) {
      setStatusIn(progressingStates);

      return;
    }

    const allIncluded = progressingStates.every(s => statusIn.includes(s));

    if (allIncluded) {
      setStatusIn(statusIn.filter(s => !progressingStates.includes(s)));
    } else {
      const toAdd = progressingStates.filter(s => !statusIn.includes(s));
      setStatusIn([...statusIn, ...toAdd]);
    }
  };

  const handleAcceptFilter = () => {
    const today = new Date();

    switch (termType) {
      case 'all': {
        setInvoiceFilter({
          ...invoiceFilter,
          page: 1,
          filter: { ...invoiceFilter.filter, statusIn, term: null },
        });

        handleRefetchInvoice({
          ...invoiceFilter,
          page: 1,
          filter: {
            ...invoiceFilter.filter,
            statusIn,
            term: null,
          },
        });

        return;
      }
      case '1month': {
        const startedAt = new Date(today);
        startedAt.setMonth(startedAt.getMonth() - 1);

        setInvoiceFilter({
          ...invoiceFilter,
          page: 1,
          filter: {
            ...invoiceFilter.filter,
            statusIn,
            term: { startedAt: startedAt.getTime(), endedAt: today.getTime() },
          },
        });

        handleRefetchInvoice({
          ...invoiceFilter,
          page: 1,
          filter: {
            ...invoiceFilter.filter,
            statusIn,
            term: { startedAt: startedAt.getTime(), endedAt: today.getTime() },
          },
        });

        return;
      }
      case '6month': {
        const startedAt = new Date(today);
        startedAt.setMonth(startedAt.getMonth() - 6);

        setInvoiceFilter({
          ...invoiceFilter,
          page: 1,
          filter: {
            ...invoiceFilter.filter,
            statusIn,
            term: { startedAt: startedAt.getTime(), endedAt: today.getTime() },
          },
        });

        handleRefetchInvoice({
          ...invoiceFilter,
          page: 1,
          filter: {
            ...invoiceFilter.filter,
            statusIn,
            term: { startedAt: startedAt.getTime(), endedAt: today.getTime() },
          },
        });

        return;
      }
      case '1year': {
        const startedAt = new Date(today);
        startedAt.setFullYear(startedAt.getFullYear() - 1);

        setInvoiceFilter({
          ...invoiceFilter,
          page: 1,
          filter: {
            ...invoiceFilter.filter,
            statusIn,
            term: { startedAt: startedAt.getTime(), endedAt: today.getTime() },
          },
        });

        handleRefetchInvoice({
          ...invoiceFilter,
          page: 1,
          filter: {
            ...invoiceFilter.filter,
            statusIn,
            term: { startedAt: startedAt.getTime(), endedAt: today.getTime() },
          },
        });

        return;
      }
      case '5year': {
        const startedAt = new Date(today);
        startedAt.setFullYear(startedAt.getFullYear() - 5);

        setInvoiceFilter({
          ...invoiceFilter,
          page: 1,
          filter: {
            ...invoiceFilter.filter,
            statusIn,
            term: { startedAt: startedAt.getTime(), endedAt: today.getTime() },
          },
        });

        handleRefetchInvoice({
          ...invoiceFilter,
          page: 1,
          filter: {
            ...invoiceFilter.filter,
            statusIn,
            term: { startedAt: startedAt.getTime(), endedAt: today.getTime() },
          },
        });

        return;
      }
      case 'custom': {
        const startedAt = new Date(customDate.start);
        const endedAt = new Date(customDate.end);

        setInvoiceFilter({
          ...invoiceFilter,
          page: 1,
          filter: {
            ...invoiceFilter.filter,
            statusIn,
            term: {
              startedAt: startedAt.getTime(),
              endedAt: endedAt.getTime(),
            },
          },
        });

        handleRefetchInvoice({
          ...invoiceFilter,
          page: 1,
          filter: {
            ...invoiceFilter.filter,
            statusIn,
            term: {
              startedAt: startedAt.getTime(),
              endedAt: endedAt.getTime(),
            },
          },
        });
        return;
      }
    }
  };

  const handleSetCustomDate = (params: Record<string, string>) => {
    setCustomDate(prev => ({
      ...prev,
      ...params,
    }));
  };

  const handleOnCloseSearchModal = () => {
    setIsOpenSearchModal(false);

    setStatusIn(invoiceFilter.filter.statusIn);
  };

  const handleResetInvoiceFilter = () => {
    setTermType('all');
    setStatusIn(requiredStatuses);
    setCustomDate({
      start: '',
      end: '',
    });

    // setInvoiceFilter({
    //   ...invoiceFilter,
    //   page: 1,
    //   filter: {
    // statusIn: requiredStatuses,
    //     term: null,
    //   },
    // });
  };

  return {
    invoices,
    invoiceFilter,
    isLoading,
    settlements,
    selectedInvoice,
    isOpenDetailModal,
    isOpenSearchModal,
    termType,
    statusIn,
    isAcceptFilter,
    customDate,
    setIsOpenSearchModal,
    handleOpenDetailModal,
    handleCloseDetailModal,
    handleLoadMoreInovice,
    handleResetInvoiceFilter,
    handleSetTermType,
    handleSetInvoiceState,
    handleSetProgressingState,
    handleAcceptFilter,
    handleOnCloseSearchModal,
    handleSetCustomDate,
  };
};
